/**
 * Typed selector hooks for the positions domain.
 *
 * Snapshot shape (from docs/ARCHITECTURE.md §3.2):
 *
 *   { as_of, mode,
 *     open: [ { symbol, instrument_type, side, quantity, avg_price, ltp,
 *               unrealised_pnl, sl_price, tp1_price, peak_price,
 *               trailing_stop, last_direction, last_confidence,
 *               last_momentum_pct, strategy?, entry_order_id?, opened_at? } ],
 *     totals: { open_count, total_unrealised_pnl, total_notional } }
 */
import { useStateStore } from "@/state/store";
import { EMPTY_ARRAY } from "@/state/hooks/_empty";

export interface PositionRow {
  symbol:             string;
  instrument_type:    "EQUITY" | "OPT" | "FUT";
  side:               "BUY" | "SELL";
  quantity:           number;
  avg_price:          number;
  ltp:                number;
  unrealised_pnl:     number;
  sl_price:           number;
  tp1_price:          number;
  tp2_price:          number;
  peak_price:         number;
  trailing_stop:      number;
  partial_tp1_done:   boolean;
  breakeven_locked:   boolean;
  tier:               number;
  entry_iv:           number;
  entry_time_unix:    number;
  last_direction:     "" | "UP" | "DOWN" | "NEUTRAL";
  last_confidence:    number;
  last_momentum_pct:  number;
  strategy?:          string | null;
  entry_order_id?:    string | null;
  opened_at?:         string | null;
  // E.4 fix (Phase J post-audit): added so PaperTradingPage renders correctly
  lot_size?:          number;
  lots?:              number;
  margin_used?:       number;
}

export interface PositionsTotals {
  open_count:           number;
  total_unrealised_pnl: number;
  total_notional:       number;
  // 2026-04-27: added when state aggregator started including closed_today
  closed_today_count?:  number;
  total_realised_pnl?:  number;
  // 2026-04-27: sum of margin_used across all positions opened today
  // (closed entries via paper_orders JOIN + open positions). Replaces the
  // UI's "currently locked margin" view which read 0 once all closed.
  total_margin_today?:  number;
}

// Closed-position rows have a different shape from open: no SL/TP/peak,
// instead exit_price + realised_pnl + closed_at.
export interface ClosedPositionRow {
  symbol:           string;
  instrument_type:  "EQUITY" | "OPT" | "FUT";
  quantity:         number;
  lots?:            number;
  avg_price:        number;
  exit_price:       number;
  ltp:              number;
  unrealised_pnl:   number;  // always 0 for closed
  realised_pnl:     number;
  strategy?:        string | null;
  close_strategy?:  string | null;
  opened_at?:       string | null;
  closed_at?:       string | null;
  status:           "CLOSED";
}

export interface PositionsSnapshot {
  as_of:        string;
  mode:         "paper" | "live";
  open:         PositionRow[];
  closed_today?: ClosedPositionRow[];
  totals:       PositionsTotals;
  _seq?:        number;
}

/** Full positions snapshot or null while hydrating. */
export function usePositions(): PositionsSnapshot | null {
  return useStateStore((s) => s.positions.snapshot as PositionsSnapshot | null);
}

/** Just the array of open positions (empty array while hydrating). */
export function usePositionsList(): PositionRow[] {
  return useStateStore((s) => {
    const snap = s.positions.snapshot as PositionsSnapshot | null;
    return (snap?.open ?? EMPTY_ARRAY) as PositionRow[];
  });
}

/** Closed-today positions (paper or live). Empty array while hydrating
 *  or if the snapshot doesn't include closed_today (older backend). */
export function useClosedPositionsList(): ClosedPositionRow[] {
  return useStateStore((s) => {
    const snap = s.positions.snapshot as PositionsSnapshot | null;
    return (snap?.closed_today ?? EMPTY_ARRAY) as ClosedPositionRow[];
  });
}

/** Aggregate totals (or null while hydrating). */
export function usePositionsTotals(): PositionsTotals | null {
  return useStateStore((s) => {
    const snap = s.positions.snapshot as PositionsSnapshot | null;
    return snap?.totals ?? null;
  });
}

/** True when WS disconnected or seq gap detected. */
export function usePositionsIsStale(): boolean {
  return useStateStore((s) => s.positions.isStale);
}

/** Date.now() of last apply, 0 if never. */
export function usePositionsLastUpdated(): number {
  return useStateStore((s) => s.positions.lastUpdatedAt);
}
